import React, { useEffect, useState } from "react";
import { Button, Checkbox, Col, Divider, Form, Input, InputNumber, Modal, Row, Select, Spin, Typography } from "antd";
import { Rule } from "antd/es/form";
import { CombustionStackInfo, EmissionTypeEnum, dropdownOption } from "src/entity/statck-info/stackInfo.entity";
import { generateErrorMessage, generateErrorMessageDropdown } from "src/utils/error-message";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";

const { Text } = Typography;

const CreateStackInfo = (props: {
  open: boolean;
  mode: "insert" | "update";
  initialData: CombustionStackInfo;
  areaMaster: dropdownOption[];
  emissionMaster: dropdownOption[];
  fuelTypeMaster: dropdownOption[];
  isDuplicate: boolean;
  loading: boolean;
  onChangeData: (isDupChange: boolean) => void;
  onCancelStackInfo: () => void;
  onSaveStackInfo: (data: CombustionStackInfo) => void;
}) => {
  const {
    open,
    initialData,
    areaMaster,
    emissionMaster,
    fuelTypeMaster,
    isDuplicate,
    loading,
    onChangeData,
    onCancelStackInfo,
    onSaveStackInfo,
  } = props;
  const [form] = Form.useForm();
  const calculationMethods: dropdownOption[] = [
    { label: "Emission Factor", value: EmissionTypeEnum.EMISSION_FACTOR },
    { label: "Stack Sampling", value: EmissionTypeEnum.STACK_SAMPLING },
  ];
  const [selectedEmissions, setSelectedEmissions] = useState<string[]>([]);
  const [isFuelDisabled, setIsFuelDisabled] = useState<boolean>(false);
  const [CombustionStackInfo, setCombustionData] = useState<CombustionStackInfo>(initialData);
  const [errorDuplicate, setErrorDuplicate] = useState<string | null>(null);

  const validateFuelName: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue("calculationMethod") === EmissionTypeEnum.EMISSION_FACTOR && !value) {
          return Promise.reject(new Error("Fuel Name is required when calculation method is emissionFactor"));
        }
        return Promise.resolve("");
      },
    }),
  ];

  const validateFuelType: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue("calculationMethod") === EmissionTypeEnum.EMISSION_FACTOR && !value) {
          return Promise.reject(new Error("Fuel Type is required when calculation method is emissionFactor"));
        }
        return Promise.resolve();
      },
    }),
  ];

  const handleChangeCalMethod = (calculationMethod: string) => {
    if (calculationMethod === "stackSampling") {
      setIsFuelDisabled(true);
      setCombustionData((prevState) => ({
        ...prevState,
        calculationMethod: calculationMethod,
        fuelType: "",
        fuelName: "",
      }));
    } else {
      setIsFuelDisabled(false);
      setCombustionData((prevState) => ({
        ...prevState,
        calculationMethod: calculationMethod,
      }));
    }
  };

  const handleChangeArea = (area: string) => {
    setCombustionData((prevState) => ({
      ...prevState,
      area: area,
    }));
  };

  const handleChangeEmissions = (checkedEmission: string[]) => {
    let emissionsUpdate: Record<string, boolean> = { ...CombustionStackInfo.emission };
    Object.keys(emissionsUpdate).forEach((key) => {
      let checked = checkedEmission.find((el) => el.toLowerCase() === key.toLowerCase());
      if (checked) {
        emissionsUpdate[key] = true;
      } else {
        emissionsUpdate[key] = false;
      }
    });
    setCombustionData({
      ...CombustionStackInfo,
      emission: { ...emissionsUpdate },
    });
    setSelectedEmissions([...checkedEmission]);
  };

  const handleChangeCombustion = (field: keyof CombustionStackInfo, value: string | number | null) => {
    if (["stackId", "sourceId", "fuelName"].includes(field)) {
      setCombustionData({
        ...CombustionStackInfo,
        [field]: String(value).trimStart(),
      });
    } else {
      setCombustionData({
        ...CombustionStackInfo,
        [field]: value,
      });
    }
  };

  const handleSaveCombustion = async () => {
    try {
      await form.validateFields();
      await onSaveStackInfo(CombustionStackInfo);
    } catch (error) {
      console.error(error);
    }
  };

  const handleInitEmission = () => {
    let emissionsUpdate: Record<string, boolean> = { ...CombustionStackInfo.emission };
    let checkedEmission: string[] = [];
    Object.keys(emissionsUpdate).forEach((key) => {
      if (emissionsUpdate[key] === true) {
        checkedEmission.push(key);
      }
    });
    setSelectedEmissions(checkedEmission);
    form.setFieldsValue({
      emissionChecked: checkedEmission,
      ...CombustionStackInfo,
    });
  };

  useEffect(() => {
    const initializeForm = async () => {
      handleInitEmission();
      handleChangeCalMethod(CombustionStackInfo?.calculationMethod);
      form.setFieldsValue(CombustionStackInfo);
    };
    initializeForm();
  }, []);

  useEffect(() => {
    form.setFieldsValue(CombustionStackInfo);
    onChangeData(false);
  }, [CombustionStackInfo, form]);

  useEffect(() => {
    if (isDuplicate) {
      if (CombustionStackInfo.calculationMethod === "stackSampling") {
        setErrorDuplicate("Stack ID , Source ID and Area aleady exists in the system.");
        form.setFields([
          {
            name: "stackId",
            errors: [""],
          },
          {
            name: "sourceId",
            errors: [""],
          },
          {
            name: "area",
            errors: [""],
          },
        ]);
      } else if (CombustionStackInfo.calculationMethod === EmissionTypeEnum.EMISSION_FACTOR) {
        setErrorDuplicate("Stack ID , Source ID , Area and Fuel Name aleady exists in the system.");
        form.setFields([
          {
            name: "stackId",
            errors: [""],
          },
          {
            name: "sourceId",
            errors: [""],
          },
          {
            name: "area",
            errors: [""],
          },
          {
            name: "fuelName",
            errors: [""],
          },
        ]);
      }
    } else {
      setErrorDuplicate("");
      form.setFields([
        {
          name: "stackId",
          errors: [],
        },
        {
          name: "sourceId",
          errors: [],
        },
        {
          name: "area",
          errors: [],
        },
        {
          name: "fuelName",
          errors: [],
        },
      ]);
    }
  }, [isDuplicate]);

  useEffect(() => {
    if (!isDuplicate) {
      form.setFields([
        {
          name: "fuelName",
          errors: [],
        },
        {
          name: "fuelType",
          errors: [],
        },
      ]);
    }
  }, [CombustionStackInfo.calculationMethod]);

  return (
    <>
      {open && (
        <Modal
          title={<ModalTitle title="Stack Information" />}
          open={open}
          width={900}
          onCancel={onCancelStackInfo}
          centered
          maskClosable={false}
          footer={
            <Row className="w-full border-t" justify={"end"}>
              <Button className="mt-4" key="submit" type="primary" onClick={handleSaveCombustion} loading={loading}>
                Save
              </Button>
            </Row>
          }
        >
          {errorDuplicate && (
            <>
              <Row className="w-full" justify={"center"}>
                <Text type="danger">{errorDuplicate}</Text>
              </Row>
            </>
          )}
          <Form
            size="middle"
            layout="vertical"
            autoComplete="off"
            form={form}
            initialValues={{ emissionChecked: selectedEmissions }}
            requiredMark={false}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Stack ID"} required />}
                  name="stackId"
                  rules={[{ required: true, message: generateErrorMessage("Stack ID") }]}
                >
                  <Input
                    style={{ width: "100%" }}
                    value={CombustionStackInfo?.stackId}
                    onChange={(e) => handleChangeCombustion("stackId", e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Source ID"} required />}
                  name="sourceId"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Source ID"),
                    },
                  ]}
                >
                  <Input
                    style={{ width: "100%" }}
                    value={CombustionStackInfo?.sourceId}
                    onChange={(e) => handleChangeCombustion("sourceId", e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Area"} required />}
                  name="area"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessageDropdown("Area"),
                    },
                  ]}
                >
                  <Select value={CombustionStackInfo.area} options={areaMaster} onChange={(value) => handleChangeArea(value)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Calculation Method"} required />}
                  name="calculationMethod"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessageDropdown("Calculation Method"),
                    },
                  ]}
                >
                  <Select
                    value={CombustionStackInfo?.calculationMethod}
                    onChange={(value) => handleChangeCalMethod(value)}
                    options={calculationMethods}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label="Fuel Name" name="fuelName" rules={validateFuelName}>
                  <Input
                    value={CombustionStackInfo?.fuelName}
                    onChange={(e) => handleChangeCombustion("fuelName", e.target.value)}
                    disabled={isFuelDisabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label="Fuel Type" name="fuelType" rules={validateFuelType}>
                  <Select
                    disabled={isFuelDisabled}
                    value={CombustionStackInfo.fuelType}
                    onChange={(value) => handleChangeCombustion("fuelType", value)}
                    options={fuelTypeMaster}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<FormLabel label={"Emission"} required />}
                  name="emissionChecked"
                  rules={[{ required: true, message: "Please select at least one emission" }]}
                >
                  <Checkbox.Group options={emissionMaster} onChange={handleChangeEmissions} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default CreateStackInfo;
